import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoIcon } from './PlatformIcons.tsx';

/** Stessa soglia di `.info-tip__panel` in `styles.css` — sotto, il pannello si stacca dall'icona. */
const NARROW_VIEWPORT = '(max-width: 30rem)';

/**
 * La spiegazione di una scheda, dietro un'icona invece che sempre in chiaro
 * (`docs/DECISIONI.md`).
 *
 * **`<details>`/`<summary>`, non un pannello nuovo.** La schermata usa già
 * questo elemento per "ciò che si legge una volta sola" (`.more`, i dati
 * tecnici dell'ente): è nativo, funziona da tastiera senza `onKeyDown`
 * scritto a mano — `Invio`/`Spazio` lo aprono, `Tab` lo raggiunge — e uno
 * screen reader annuncia da solo "compresso"/"espanso", senza `aria-expanded`
 * gestito a mano. Introdurre un secondo meccanismo (un popover al passaggio
 * del mouse con `onMouseEnter`/`onFocus`) avrebbe voluto dire due modi
 * diversi di fare la stessa cosa nella stessa schermata.
 *
 * **Non per l'aiuto di un campo.** Il contenuto di un `<details>` chiuso non
 * entra nell'albero di accessibilità finché non si apre: un campo che lo
 * punta con `aria-describedby` non avrebbe più l'aiuto letto da uno screen
 * reader arrivando sul campo, che è il requisito esplicito della sezione
 * 7bis. Per questo l'aiuto dei campi resta un `<p className="muted">` sempre
 * visibile, o passa da `FieldHint.tsx` (che risolve lo stesso problema in
 * modo diverso) — solo la spiegazione delle schede, che non è collegata a
 * nessun campo, passa da qui.
 *
 * **Si chiude anche cliccando fuori, e con `Esc`.** Prima restava aperto
 * finché non si ri-premeva la stessa icona: su un modulo lungo, aprirne una
 * seconda e poi lavorare altrove lasciava il primo pannello sospeso sopra il
 * contenuto. Stesso identico schema di `AccountMenu.tsx` — `pointerdown`
 * fuori dal `<details>` (non `click`, che arriva dopo che il bersaglio ha
 * già reagito) più `Esc`, col fuoco che torna sul pulsante — riusato invece
 * di reinventarlo.
 *
 * **Sotto i 30rem, il pannello segue l'icona, non il fondo dello schermo.**
 * `.info-tip__panel` resta `position: fixed` a quella soglia (serve per non
 * uscire dal bordo destro del viewport con un titolo lungo, WCAG 1.4.10,
 * `docs/DECISIONI.md` — un pannello ancorato all'icona con `position:
 * absolute` sconfinerebbe comunque quando l'icona è vicina al bordo), ma
 * `fixed` da solo non sa **dove** stare verticalmente: prima restava incollato
 * al fondo della pagina, dove su un telefono con barra di navigazione
 * trasparente finiva coperto. Qui si calcola la posizione vera dell'icona
 * (`getBoundingClientRect`) e si scrive `top` inline solo per questo caso —
 * sopra i 30rem lo stile resta quello statico del foglio (l'orizzontale resta
 * sempre `inset-inline: var(--s4)` da CSS, mai calcolato qui). Si azzera lo
 * stesso `top` appena il viewport smette di essere stretto — un pannello
 * aperto una volta sotto i 30rem e riaperto più largo, senza ricaricare la
 * pagina, restava ancorato al vecchio valore in pixel invece che tornare allo
 * stile statico del foglio (`code-reviewer`, in revisione). Si richiude anche
 * scorrendo, invece di rincorrere la posizione a ogni evento di scroll: un
 * pannello che segue il dito è più invasivo di uno che sparisce, e chiudere è
 * lo stesso gesto che chiude cliccando fuori. Ricalcola anche al
 * ridimensionamento della finestra mentre resta aperto (raro con un
 * telefono, non impossibile con un browser desktop ridimensionato a mano).
 */
export function InfoTip({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const summaryRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLParagraphElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const closeFromOutside = (event: Event): void => {
      const target = event.target;
      if (target instanceof Node && detailsRef.current?.contains(target)) return;
      setOpen(false);
    };

    const closeFromKeyboard = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      summaryRef.current?.focus();
    };

    /**
     * Solo lo scorrimento della pagina, o di un contenitore che avvolge
     * *questo* pannello — non lo scorrimento di un riquadro qualunque altrove
     * nello schermo. L'ascolto sta in `capture: true` perché lo scroll non
     * risale (`bubbles: false`) e senza la fase di cattura un contenitore con
     * `overflow-y: auto` proprio non lo farebbe mai arrivare qui — ma quello
     * stesso motivo lo fa arrivare anche dallo scorrimento di un riquadro
     * scorrevole **non correlato** (es. `.usermenu__panel`), che altrimenti
     * chiuderebbe un InfoTip aperto altrove nella pagina senza nessun motivo
     * visibile per chi guarda (`accessibility`, in revisione).
     */
    const closeFromScroll = (event: Event): void => {
      const target = event.target;
      const isPage = target === document || target === document.documentElement;
      const isAncestor = target instanceof Node && target.contains(detailsRef.current);
      if (!isPage && !isAncestor) return;
      setOpen(false);
    };

    document.addEventListener('pointerdown', closeFromOutside);
    document.addEventListener('keydown', closeFromKeyboard);
    document.addEventListener('scroll', closeFromScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('pointerdown', closeFromOutside);
      document.removeEventListener('keydown', closeFromKeyboard);
      document.removeEventListener('scroll', closeFromScroll, { capture: true });
    };
  }, [open]);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const summary = summaryRef.current;
    if (!open || !panel || !summary) return;

    const place = (): void => {
      if (!globalThis.matchMedia?.(NARROW_VIEWPORT).matches) {
        // Non più stretto: torna allo stile statico del foglio, non al
        // valore in pixel dell'ultima volta che lo era.
        panel.style.top = '';
        return;
      }

      const gap = 8; // var(--s1) in pixel, il divario già usato sopra i 30rem
      const margin = 16; // var(--s4), lo stesso margine di .info-tip__panel
      const iconRect = summary.getBoundingClientRect();
      const panelHeight = panel.offsetHeight;
      const spaceBelow = globalThis.innerHeight - iconRect.bottom;
      const top =
        spaceBelow >= panelHeight + gap
          ? iconRect.bottom + gap
          : Math.max(margin, iconRect.top - panelHeight - gap);

      panel.style.top = `${top}px`;
    };

    place();
    globalThis.addEventListener('resize', place);
    return () => {
      globalThis.removeEventListener('resize', place);
      panel.style.top = '';
    };
  }, [open]);

  return (
    <details
      ref={detailsRef}
      className="info-tip"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary ref={summaryRef} aria-label={t('platform.more_info')}>
        <InfoIcon />
      </summary>
      <p ref={panelRef} className="info-tip__panel">
        {children}
      </p>
    </details>
  );
}
